import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

/* ─── Animations ─── */
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const progressGrow = keyframes`
  from { width: 0; }
  to   { width: 100%; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

/* ─── Styled components ─── */
const StyledLoader = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%; height: 100%;
  background-color: var(--dark-navy);
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  transition: opacity 0.4s ease;

  &.fade-out {
    animation: ${fadeOut} 0.4s ease forwards;
  }
`;

const Terminal = styled.div`
  width: 520px;
  max-width: 90vw;
  background: var(--navy);
  border: 1px solid var(--lightest-navy);
  border-radius: var(--border-radius);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

const TitleBar = styled.div`
  background: var(--light-navy);
  padding: 10px 15px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--lightest-navy);

  .dots {
    display: flex;
    gap: 8px;
    span {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      display: inline-block;
    }
    .red    { background-color: #ff5f56; }
    .yellow { background-color: #3a3a3c; }
    .green  { background-color: #27c93f; }
  }

  .title {
    flex: 1;
    text-align: center;
    color: var(--slate);
    font-size: 12px;
    user-select: none;
  }
`;

const Content = styled.div`
  padding: 24px 20px;
  min-height: 180px;
  font-size: 13px;
  line-height: 1.8;
  color: var(--light-slate);
`;

const Line = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.3s ease forwards;
  animation-delay: ${props => props.delay}ms;
  display: flex;
  align-items: baseline;
  gap: 6px;

  .prompt {
    color: var(--green);
    user-select: none;
  }

  .cmd {
    color: var(--lightest-slate);
  }

  .ok {
    color: var(--green);
    font-weight: 600;
  }

  .info {
    color: var(--blue);
  }

  .dim {
    color: var(--slate);
  }
`;

const ProgressBarWrap = styled.div`
  margin-top: 8px;
  opacity: 0;
  animation: ${fadeIn} 0.3s ease forwards;
  animation-delay: ${props => props.delay}ms;

  .bar-label {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: var(--slate);
    margin-bottom: 4px;
  }

  .bar-track {
    width: 100%;
    height: 4px;
    background: var(--lightest-navy);
    border-radius: 2px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: var(--green);
    border-radius: 2px;
    animation: ${progressGrow} ${props => props.duration}ms ease forwards;
    animation-delay: ${props => props.delay + 200}ms;
    width: 0;
  }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 7px;
  height: 14px;
  background: var(--green);
  vertical-align: text-bottom;
  margin-left: 2px;
  animation: ${blink} 1s step-end infinite;
`;

/* ─── Boot sequence lines ─── */
const BOOT_LINES = [
  { type: 'cmd', text: 'initializing portfolio...' },
  { type: 'info', text: '[OK] modules loaded' },
  { type: 'info', text: '[OK] components compiled' },
  { type: 'info', text: '[OK] styles injected' },
];

const LINE_INTERVAL = 350;        // ms between each line
const PROGRESS_START = BOOT_LINES.length * LINE_INTERVAL + 100;
const PROGRESS_DURATION = 1200;   // how long the bar takes to fill
const TOTAL_DURATION = PROGRESS_START + PROGRESS_DURATION + 600; // + pause before exit

/* ─── Component ─── */
const Loader = ({ finishLoading }) => {
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadingOut(true);
      setTimeout(finishLoading, 400); // matches fade-out duration
    }, TOTAL_DURATION);
    return () => clearTimeout(timer);
  }, []);

  return (
    <StyledLoader className={fadingOut ? 'fade-out' : ''}>
      <Helmet bodyAttributes={{ class: 'hidden' }} />

      <Terminal>
        <TitleBar>
          <div className="dots">
            <span className="red" />
            <span className="yellow" />
            <span className="green" />
          </div>
          <div className="title">keerthan — portfolio</div>
        </TitleBar>

        <Content>
          {BOOT_LINES.map((line, i) => (
            <Line key={i} delay={i * LINE_INTERVAL}>
              <span className="prompt">❯</span>
              {line.type === 'cmd' ? (
                <span className="cmd">{line.text}</span>
              ) : (
                <span className="ok">{line.text}</span>
              )}
            </Line>
          ))}

          <ProgressBarWrap delay={PROGRESS_START} duration={PROGRESS_DURATION}>
            <div className="bar-label">
              <span>building...</span>
              <span>done</span>
            </div>
            <div className="bar-track">
              <div className="bar-fill" />
            </div>
          </ProgressBarWrap>

          <Line delay={PROGRESS_START + PROGRESS_DURATION + 200}>
            <span className="prompt">❯</span>
            <span className="ok">ready</span>
            <Cursor />
          </Line>
        </Content>
      </Terminal>
    </StyledLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
